import { forwardRef, MouseEventHandler } from "react";
import DatePicker, { DatePickerProps } from "react-datepicker";
import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  ResponsiveValue,
  SystemStyleObject,
  useTheme
} from "@chakra-ui/react";
import { FiCalendar } from "react-icons/fi";

import type { Locale } from "date-fns";

import "react-datepicker/dist/react-datepicker.css";

type CalendarProps = DatePickerProps & {
  size?: ResponsiveValue<(string & unknown) | "sm" | "md" | "lg" | "xs">;
  ariaLabel?: string;
  isInsideIframe?: boolean;
  fontSize?: ResponsiveValue<string & unknown>;
};

function Calendar(props: CalendarProps) {
  const { size, locale = "en-AU", isInsideIframe, fontSize } = props;
  const { fonts } = useTheme();

  const CalendarStyles: SystemStyleObject = {
    ".react-datepicker *": {
      fontFamily: fonts?.body,
      outlineColor: "brand.500"
    },

    ".react-datepicker-wrapper *": {
      fontFamily: fonts?.body
    },

    ".react-datepicker-wrapper": {
      width: "100%"
    },

    ".react-datepicker": {
      border: "none",
      boxShadow: "lg"
    },

    ".react-datepicker__header": {
      bg: "gray.100",
      borderBottom: "none"
    },

    ".react-datepicker__current-month": {
      mt: 1.5,
      fontSize: "md",
      color: "gray.800"
    },

    ".react-datepicker__header__dropdown": {
      mt: 2.5,
      display: "flex",
      justifyContent: "space-between"
    },

    ".react-datepicker__navigation": {
      top: 3.5
    },

    ".react-datepicker__navigation-icon--next::before": {
      borderColor: "gray.800",

      ":hover": {
        borderColor: "gray.900"
      }
    },

    ".react-datepicker__navigation-icon--previous::before": {
      borderColor: "gray.800",

      ":hover": {
        borderColor: "gray.900"
      }
    },

    ".react-datepicker__header__dropdown select": {
      bg: "gray.100",
      fontSize: "sm",
      fontWeight: 500,
      color: "gray.800"
    },

    ".react-datepicker__close-icon": {
      right: size === "sm" ? 10 : size === "md" ? "45px" : "57px",
      top: size === "sm" ? 2 : size === "md" ? 2.5 : 3,
      height: size === "sm" ? 4 : size === "md" ? 5 : 6,
      width: size === "sm" ? 4 : size === "md" ? 5 : 6,
      padding: 0,
      display: "inline-block"
    },

    ".react-datepicker__close-icon::after": {
      bg: "transparent",
      fontSize: size === "sm" ? "2xl" : size === "md" ? "3xl" : "40px",
      height: size === "sm" ? 4 : size === "md" ? 5 : 6,
      width: size === "sm" ? 4 : size === "md" ? 5 : 6,
      padding: 0,
      lineHeight: 0,
      color: "currentColor"
    },
    ".chakra-input__right-element": {
      height: size === "sm" ? 4 : size === "md" ? 5 : 6,
      width: size === "sm" ? 4 : size === "md" ? 5 : 6,
      top: size === "sm" ? 2 : size === "md" ? 2.5 : 3.5,
      right: size === "sm" ? 2 : size === "md" ? "9px" : "9px"
    },
    ".react-datepicker__day-name, .react-datepicker__day, .react-datepicker__time-name":
      {
        w: 8,
        lineHeight: "2rem",
        color: "gray.800",

        ":hover": {
          bg: "gray.100"
        }
      },

    ".react-datepicker__day--outside-month": {
      color: "gray.500"
    },

    ".react-datepicker__day--keyboard-selected, .react-datepicker__month-text--keyboard-selected, .react-datepicker__quarter-text--keyboard-selected, .react-datepicker__year-text--keyboard-selected":
      {
        bg: "brand.50",

        ":hover": {
          bg: "brand.200"
        },

        ":focus": {
          outline: "none"
        }
      },

    ".react-datepicker__day--selected, .react-datepicker__day--in-selecting-range, .react-datepicker__day--in-range, .react-datepicker__month-text--selected, .react-datepicker__month-text--in-selecting-range, .react-datepicker__month-text--in-range, .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul .react-datepicker__time-list li .react-datepicker__time-list-item--selected":
      {
        bg: "brand.500",
        color: "white",

        ":hover": {
          bg: "brand.600"
        }
      },
    svg: {
      width: size === "sm" ? 4 : size === "md" ? 5 : 6,
      height: size === "sm" ? 4 : size === "md" ? 5 : 6,
      color: "currentColor"
    },
    ".react-datepicker__portal": {
      background: "blackAlpha.600"
    }
  };

  const getLocale = (): Locale => {
    return require(`date-fns/locale/${locale}/index.js`);
  };

  const ChakraUIInput = forwardRef<
    HTMLInputElement,
    { onClick?: MouseEventHandler<HTMLDivElement> }
  >((props, ref) => (
    <InputGroup>
      <Input {...props} type="text" ref={ref} size={size} fontSize={fontSize} />
      <InputRightElement children={<FiCalendar />} onClick={props.onClick} />
    </InputGroup>
  ));

  return (
    <Box sx={CalendarStyles}>
      <DatePicker
        {...props}
        isClearable
        showYearDropdown
        showMonthDropdown
        dropdownMode="select"
        showPopperArrow={false}
        customInput={<ChakraUIInput aria-label={props.ariaLabel} />}
        locale={getLocale()}
        withPortal={isInsideIframe}
      />
    </Box>
  );
}

export { Calendar };
export type { CalendarProps };
